import { Pipe, PipeTransform } from '@angular/core';

/**
 * The two text renderers of the provider template (`md()` and `ownText()`), as pure
 * parsers that produce a STRUCTURE the component template walks with `@for`. Nothing
 * here returns HTML and nothing is ever bound through innerHTML: every piece of text
 * ends up interpolated, so markup in an answer is shown literally.
 *
 * They are kept apart on purpose, as in the template. Our own copy (the greeting) may
 * carry `**bold**`; an answer from the service may not, because whatever we let through
 * there is something the model could write. Do not merge them.
 */

/** A run of text, or a citation marker `[n]` (n is 1-based: `[n]` is `sources[n-1]`). */
export type DrfSegment = { text: string; cite?: undefined } | { cite: number; text?: undefined };
export type DrfLine = DrfSegment[];
export type DrfBlock = { kind: 'p'; lines: DrfLine[] } | { kind: 'ul'; items: DrfLine[] };

/** Our own copy: paragraphs of lines of plain/bold runs. */
export interface DrfOwnSegment { text: string; bold: boolean; }
export type DrfOwnParagraph = DrfOwnSegment[][];

const BULLET = /^\s*-\s+/;
const CITE = /\[(\d{1,3})\]/g;

/**
 * Splits one line of an answer into text and citation segments. Stray `**` are removed
 * rather than printed — the service strips Markdown, so a prompt change must never put
 * literal asterisks in front of a member. A marker pointing past the source list stays
 * text: there is nothing to jump to.
 */
function inline(line: string, sourceCount: number): DrfLine {
  const s = line.replace(/\*\*/g, '');
  if (sourceCount <= 0) return [{ text: s }];
  const out: DrfLine = [];
  let last = 0;
  for (const match of s.matchAll(CITE)) {
    const n = Number(match[1]);
    if (n < 1 || n > sourceCount) continue;
    const at = match.index ?? 0;
    if (at > last) out.push({ text: s.slice(last, at) });
    out.push({ cite: n });
    last = at + match[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last) });
  return out;
}

/**
 * What survives the service's plain-text pass: blank-line separated blocks, `- ` bullet
 * lines (a block made only of them is a list) and `[n]` citation markers.
 */
export function parseAnswerText(text: string | null | undefined, sourceCount: number): DrfBlock[] {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return [];
  return trimmed.split(/\n\s*\n/).map(block => {
    const lines = block.split('\n');
    if (lines.every(l => BULLET.test(l))) {
      return { kind: 'ul' as const, items: lines.map(l => inline(l.replace(BULLET, ''), sourceCount)) };
    }
    return { kind: 'p' as const, lines: lines.map(l => inline(l, sourceCount)) };
  });
}

/** Our own text with `**bold**` allowed — the greeting, never an answer. */
export function parseOwnText(text: string | null | undefined): DrfOwnParagraph[] {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return [];
  return trimmed.split(/\n\s*\n/).map(block =>
    block.split('\n').map(line => {
      const segs: DrfOwnSegment[] = [];
      const re = /\*\*(.+?)\*\*/g;
      let last = 0;
      for (const m of line.matchAll(re)) {
        const at = m.index ?? 0;
        if (at > last) segs.push({ text: line.slice(last, at), bold: false });
        segs.push({ text: m[1], bold: true });
        last = at + m[0].length;
      }
      if (last < line.length) segs.push({ text: line.slice(last), bold: false });
      return segs;
    }));
}

/** Length of an answer as read aloud — drives how long the avatar "talks". */
export function plainLength(text: string | null | undefined): number {
  return (text ?? '')
    .replace(/\*\*/g, '').replace(/\[\d+\]/g, '').replace(/^\s*-\s+/gm, '').replace(/\s+/g, ' ').trim().length;
}

/**
 * The short origin of a source ("dailymed"), derived from the URL host because the
 * endpoint only gives the full issuer name, which does not fit the line. The full
 * name stays available as the tooltip.
 */
export function shortOrigin(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const host = new URL(url, window.location.href).hostname.replace(/^www\./, '');
    return host.split('.')[0] || null;
  } catch {
    return null;
  }
}

/** `answerText | drfAnswer: sourceCount` → blocks (pure: recomputed only when the text changes). */
@Pipe({ name: 'drfAnswer', standalone: true })
export class DrfAnswerPipe implements PipeTransform {
  transform(text: string | null | undefined, sourceCount = 0): DrfBlock[] {
    return parseAnswerText(text, sourceCount);
  }
}

/** `('key' | translate) | drfOwnText` → paragraphs (pure: re-parsed when the language changes the string). */
@Pipe({ name: 'drfOwnText', standalone: true })
export class DrfOwnTextPipe implements PipeTransform {
  transform(text: string | null | undefined): DrfOwnParagraph[] {
    return parseOwnText(text);
  }
}
